<template>
  <multiselect
    v-model="value"
    v-bind="$attrs"
    :allow-empty="$attrs['allow-empty'] || false"
    :placeholder="inputLabel ? '' : $attrs.placeholder || ''"
    :searchable="$attrs.searchable || false"
    select-label=""
    selected-label=""
    select-group-label=""
    deselect-label=""
    deselect-group-label=""
    class="yt-input-select"
    :class="{
      '_input-label': inputLabel,
      '_has-value': hasValue,
      _error: error,
    }"
  >
    <template #caret>
      <div
        v-if="inputLabel"
        class="yt-input-select__input-label"
        v-html="inputLabel"
      ></div>
      <button
        type="button"
        class="yt-input-select__caret u-decor u-flex-shrink-0"
      >
        <svg-inline tag="span" dir="down" class="u-stroke-deep-current">
          <icon icon="icon-angle-up" />
        </svg-inline>
      </button>
    </template>
    <template #noOptions>
      <span v-html="$t('multiselect.noOptions')"></span>
    </template>
    <template #noResult>
      <span v-html="$t('multiselect.noResult')"></span>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from "vue-multiselect";
import SvgInline from "@/components/ui-kit/svg-inline/index.vue";
import Icon from "@/components/icons/index.vue";

export default {
  components: {
    Multiselect,
    SvgInline,
    Icon,
  },
  inheritAttrs: false,
  model: {
    prop: "modelValue",
    event: "change",
  },
  props: {
    modelValue: {
      validator: (prop) =>
        typeof prop === "string" ||
        typeof prop === "number" ||
        typeof prop === "object",
      required: true,
    },
    inputLabel: {
      typ: String,
      default: "",
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    value: {
      get() {
        return this.modelValue;
      },
      set(newValue) {
        this.$emit("change", newValue);
      },
    },
    hasValue() {
      if (Array.isArray(this.modelValue)) {
        return !!this.modelValue.length;
      }

      return !!this.modelValue;
    },
  },
};
</script>

<style lang="scss">
@import "~vue-multiselect/dist/vue-multiselect.min.css";

$b: ".yt-input-select";
$m: ".multiselect";
$caret-width: 56px;

#{$b} {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  min-height: 0;
  color: var.$black;
  border: 1px solid var.$gray-dark;
  border-radius: var.$rounded-8;
  font-family: var.$font-family-sf-pro-text;
  box-sizing: border-box;
  transition: var.$transition-fast;

  &__caret {
    @include mixin.button-reset();
    width: $caret-width;
    padding: 17px 18px;
    color: var.$secondary;
    transition: var.$transition-default;

    #{$b}._has-value &,
    #{$m}--active & {
      color: var.$black;
    }

    #{$m}--active & {
      transform: rotate(-180deg);
    }
  }

  &__input-label {
    @include mixin.decor();
    @include mixin.font-regular();
    @include mixin.truncate-text();
    color: var.$secondary;
    position: absolute;
    top: 50%;
    right: $caret-width;
    left: 16px;
    transform: translate(0, -50%);
    transform-origin: left top;
    transition: var.$transition-fast;

    #{$b}._has-value &,
    #{$m}--active & {
      transform: translate(0, -50%) translate(0, -9px)
        scale(func.divide(12, 14));
      right: calc(#{$caret-width} - #{percentage(func.divide(14 - 12, 14))});
    }
  }

  &#{$m} {
    display: flex;
  }

  #{$m} {
    &__tags {
      flex-grow: 1;
      min-width: 1px;
      padding: 0 0 0 16px;
      min-height: 0;
      border: none;
      background: none;
    }

    &__input,
    &__single {
      @include mixin.font-regular();
      min-height: 0;
      background: none;
      padding: 0;
      margin: 0;
      transition: var.$transition-fast;
      transition-property: color;
    }

    &__placeholder {
      @include mixin.font-regular();
      padding: 0;
      margin: 0;
    }

    &__content {
      padding: 8px;

      &-wrapper {
        width: auto;
        top: calc(100% + 8px);
        right: -1px;
        left: -1px;
        box-shadow: 1px 4px 20px rgba(var.$secondary, 0.35);
        border: none;
        border-radius: var.$rounded-12;
      }
    }

    &__element {
      padding: 0;
    }

    &__option {
      @include mixin.font-semibold();
      border-radius: 6px;
      padding: 11px 12px;
      min-height: 0;
      color: var.$black;
      transition: var.$transition-default;

      &--selected {
        background: var.$green-main;

        &:not(#{$m}__option--highlight) {
          color: var.$white;
        }
      }

      &--highlight {
        background: var.$gray-pale;
      }
    }
  }

  &._input-label {
    #{$m} {
      &__input,
      &__single {
        margin-top: 17px;
      }
    }
  }

  &._error {
    border-color: var.$red;

    #{$b} {
      &__input-label {
        color: var.$red;
      }
    }
  }

  &#{$m} {
    &--above {
      #{$m} {
        &__content-wrapper {
          top: auto;
          bottom: calc(100% + 8px);
        }
      }
    }

    &--disabled {
      @include mixin.decor();
      border-color: var.$gray-dark;
      background: var.$gray-pale;

      #{$m} {
        &__input,
        &__single {
          color: var.$secondary;
        }
      }

      #{$b} {
        &__input-label {
          color: var.$secondary;
        }

        &__caret {
          color: var.$secondary;
        }
      }
    }
  }
}
</style>
